import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@hooks/useAuth';
import Icon from '@components/common/atoms/Icon';
import { getAllEmployees, EmployeeListItem } from '../services/employeeManagementService';
import { AppLoader } from '@/components/common/atoms/AppLoader';

interface EmployeeSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EmployeeSearchModal: React.FC<EmployeeSearchModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<EmployeeListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Reset state and focus the input when the modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setResults([]);
      setHasSearched(false);
      // Focus after the modal has rendered
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const fetchEmployees = useCallback(
    async (term: string) => {
      if (!user?.tenantId) return;
      try {
        setLoading(true);
        const result = await getAllEmployees({
          tenantId: user.tenantId,
          pageNumber: 1,
          pageSize: 8,
          searchTerm: term,
        });
        setResults(result.items);
      } catch (error) {
        console.error('Employee search failed:', error);
        setResults([]);
      } finally {
        setLoading(false);
        setHasSearched(true);
      }
    },
    [user?.tenantId]
  );

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;
    const trimmed = searchTerm.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setHasSearched(false);
      return;
    }
    const timer = setTimeout(() => fetchEmployees(trimmed), 350);
    return () => clearTimeout(timer);
  }, [searchTerm, isOpen, fetchEmployees]);

  const handleSelect = (employee: EmployeeListItem) => {
    onClose();
    navigate(`/settings/users/${employee.id}`);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[10vh] bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <Icon name="Search" size={20} className="text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search employees by name, email or code..."
            className="flex-1 bg-transparent text-slate-900 dark:text-white placeholder-slate-400 outline-none text-base"
          />
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <Icon name="X" size={18} className="text-slate-400" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[50vh] overflow-y-auto">
          {loading ? (
            <AppLoader size="small" message="Searching..." className="py-10" />
          ) : searchTerm.trim().length < 2 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-400 dark:text-slate-500">
              Type at least 2 characters to search for an employee.
            </div>
          ) : results.length === 0 && hasSearched ? (
            <div className="flex flex-col items-center justify-center py-10 text-slate-400 dark:text-slate-500">
              <Icon name="UserX" size={32} className="mb-3 opacity-60" />
              <p className="text-sm">No employees found for "{searchTerm.trim()}"</p>
            </div>
          ) : (
            <ul className="py-2">
              {results.map((employee) => (
                <li key={employee.id}>
                  <button
                    onClick={() => handleSelect(employee)}
                    className="w-full flex items-center gap-3 px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors text-left group"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
                      {employee.fullName.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 dark:text-white truncate">
                        {employee.fullName}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                        {employee.email}
                        {employee.jobTitle ? ` · ${employee.jobTitle}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="hidden sm:inline text-xs text-slate-400 dark:text-slate-500">
                        {employee.employeeCode}
                      </span>
                      <Icon
                        name="ChevronRight"
                        size={16}
                        className="text-slate-300 dark:text-slate-600 group-hover:text-indigo-500 transition-colors"
                      />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeSearchModal;
